import { useState, useEffect, useMemo, useCallback } from 'react';
import { DefaultErrorState } from 'components/common/Payable';
import DepositComponent from './Deposit';
import { DepositOptions } from './types';
import './DepositPayable.scss';
import { ErrorState } from 'types/Payable';
import {
  AgxOnOrDaysFrom,
  AgxSpacer,
  ContractDateType,
} from '@urbanx/agx.ui-components2';
import {
  Deposit,
  DepositCondition,
  DepositConditionType,
  DepositDayType,
  DepositPayable,
  DepositType,
  FormCondition,
  FormConditionCondition,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';

interface DepositPayableProps {
  id: string;
  label: string;
  onValueChanged: (value: { id: string; value: Deposit }) => void;
  defaultValue: Deposit;
  required?: boolean;
  validate?: boolean;
  error?: ErrorState;
  campaignId: string;
  specialConditions: SpecialConditionData[];
}

const conditionsToInclude = [
  FormConditionCondition.OnSpecificDate,
  FormConditionCondition.FinanceBeingSatisfied,
  FormConditionCondition.BuildingAndPestBeingSatisfied,
  FormConditionCondition.UponSatisfactionOfSpecialCondition,
];

const DepositPayableComponent = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  required = false,
  validate = false,
  error = DefaultErrorState,
  campaignId,
  specialConditions,
}: DepositPayableProps) => {
  const [selectedCondition, setSelectedCondition] = useState<
    DepositCondition | undefined
  >(defaultValue?.condition);

  const [depositValue, setDepositValue] = useState<Deposit>({
    ...defaultValue,
    isDepositSelected: defaultValue?.isDepositSelected,
    depositType: defaultValue?.depositType,
    depositValueType: defaultValue?.depositValueType ?? DepositOptions[0].value,
    condition: selectedCondition,
    dayType: defaultValue?.dayType ?? DepositDayType.Days,
    depositPayable:
      defaultValue?.depositPayable ||
      (defaultValue?.depositType === DepositType.Initial
        ? DepositPayable.On
        : DepositPayable.NDaysFrom),
    specialConditionId: defaultValue?.specialConditionId,
  });

  const isInitialDeposit = useMemo(
    () => depositValue.depositType === DepositType.Initial,
    [depositValue.depositType]
  );

  const payableLabel = useMemo(() => {
    return `${depositValue.depositType} Deposit Payable`;
  }, [depositValue.depositType]);

  const hasAmountOrPercent = useMemo(
    () => !!depositValue.amount || !!depositValue.percent,
    [depositValue.amount, depositValue.percent]
  );

  const isConditionUnchecked = useMemo(
    () => !depositValue.condition,
    [depositValue.condition]
  );

  const showBalanceDepositValidation = useMemo(() => {
    return (
      !isInitialDeposit &&
      ((isConditionUnchecked && hasAmountOrPercent) ||
        (!isConditionUnchecked && !hasAmountOrPercent))
    );
  }, [isInitialDeposit, isConditionUnchecked, hasAmountOrPercent]);

  const conditionTypeCheck = useMemo(
    () =>
      id === DepositType.Initial
        ? DepositConditionType.Contract
        : DepositConditionType.Unconditional,
    [id]
  );

  useEffect(() => {
    if (!depositValue) return;

    let condition = depositValue.condition;
    if (isInitialDeposit && !depositValue?.condition) {
      setSelectedCondition(DepositCondition.OnDate);
    }
    onValueChanged({
      id,
      value: {
        ...depositValue,
        condition: condition,
        depositType: depositValue.depositType,
        conditionType: conditionTypeCheck,
      },
    });
  }, [depositValue]);

  useEffect(() => {
    if (!selectedCondition) return;

    setDepositValue(prev => ({ ...prev, condition: selectedCondition }));
  }, [selectedCondition]);

  const onConditionChange = useCallback((value: FormCondition) => {
    setDepositValue(prev => ({
      ...prev,
      condition:
        (value.condition as unknown as DepositCondition) ??
        DepositCondition.OnDate,
      daysFromDate: value.daysFromDate,
      depositPayable: value.depositPayable,
      dayType: value.dayType as unknown as DepositDayType,
      specialConditionId: value.specialConditionId,
      referenceDate: value.referenceDate,
      conditionType: conditionTypeCheck,
      other: value.other,
    }));
  }, []);

  return (
    <AgxSpacer direction="vertical" gap="lg" fill>
      <DepositComponent
        key={id}
        id={id}
        label={label}
        onValueChanged={({ value }) => {
          setDepositValue(prev => ({
            ...prev,
            depositValueType: value.depositValueType,
            amount: value.amount,
            percent: value.percent,
          }));
        }}
        defaultValue={depositValue}
        required={required}
        balanceDepositValidation={showBalanceDepositValidation}
        error={error}
        validate={validate}
      />
      <AgxOnOrDaysFrom
        id={id}
        label={payableLabel}
        condition={depositValue as FormCondition}
        conditionsToIncludeInDaysFrom={conditionsToInclude}
        conditionsToIncludeInOn={conditionsToInclude}
        specialConditions={specialConditions}
        campaignId={campaignId}
        validate={validate}
        defaultCondition={FormConditionCondition.OnDate}
        invertSegmentOrder={isInitialDeposit}
        contractDateType={
          conditionTypeCheck === DepositConditionType.Contract
            ? ContractDateType.ContractDate
            : ContractDateType.UnconditionalDate
        }
        onChange={onConditionChange}
        onIsValidChange={() => {}}
      />
    </AgxSpacer>
  );
};

export default DepositPayableComponent;
